import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function AddArtist() {
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);

    const [form, setForm] = useState({
        name: '',
        city: '',
        country_id: '',
        state_id: '',
        artist_type_id: '',
        artist_style_id: '',
        description: '',
        image_url: '',
    });

    const [formerlyKnownAs, setFormerlyKnownAs] = useState([]);
    const [hometowns, setHometowns] = useState([]);
    const [links, setLinks] = useState([]);

    useEffect(() => {
        axios.get(`${config.API_URL}/picklists`)
            .then(res => {
                setPicklists(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load form data');
                setLoading(false);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country_id' ? { state_id: '' } : {})
        }));
    };

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    // --- Formerly Known As ---
    const addFka = () => setFormerlyKnownAs(prev => [...prev, { name: '' }]);
    const updateFka = (index, value) => {
        setFormerlyKnownAs(prev => prev.map((f, i) => i === index ? { name: value } : f));
    };
    const removeFka = (index) => setFormerlyKnownAs(prev => prev.filter((_, i) => i !== index));

    // --- Hometowns ---
    const addHometown = () => setHometowns(prev => [...prev, { city: '', country_id: '', state_id: '' }]);
    const updateHometown = (index, field, value) => {
        setHometowns(prev => prev.map((ht, i) => {
            if (i !== index) return ht;
            return { ...ht, [field]: value, ...(field === 'country_id' ? { state_id: '' } : {}) };
        }));
    };
    const removeHometown = (index) => setHometowns(prev => prev.filter((_, i) => i !== index));

    const getFilteredStatesForHometown = (country_id) => {
        if (!picklists || !country_id) return [];
        return picklists.states_regions.filter(s => s.country_id === parseInt(country_id));
    };

    // --- Links ---
    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (index, field, value) => {
        setLinks(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
    };
    const removeLink = (index) => setLinks(prev => prev.filter((_, i) => i !== index));

    const handleSubmit = async () => {
        if (!form.name) {
            setError('Artist name is required');
            return;
        }
        setSaving(true);
        setError(null);
        setDupWarning(null);
        try {
            const payload = {
                name: form.name,
                city: form.city || null,
                country_id: form.country_id ? parseInt(form.country_id) : null,
                state_id: form.state_id ? parseInt(form.state_id) : null,
                artist_type_id: form.artist_type_id ? parseInt(form.artist_type_id) : null,
                artist_style_id: form.artist_style_id ? parseInt(form.artist_style_id) : null,
                description: form.description || null,
                image_url: form.image_url || null,
                formerly_known_as: formerlyKnownAs.filter(f => f.name.trim()),
                hometowns: hometowns
                    .filter(ht => ht.city || ht.state_id || ht.country_id)
                    .map(ht => ({
                        city: ht.city || null,
                        state_id: ht.state_id ? parseInt(ht.state_id) : null,
                        country_id: ht.country_id ? parseInt(ht.country_id) : null,
                    })),
                links: links.filter(l => l.description.trim() && l.url.trim()),
            };
            const res = await axios.post(`${config.API_URL}/artists`, payload);
            navigate(`/artists/${res.data.id}`);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else {
                setError('Failed to save artist');
            }
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to="/artists" style={styles.backLink}>← Back to Artists</Link>
            <h1 style={styles.title}>Add Artist</h1>

            {error && <div style={styles.errorBox}>{error}</div>}
            {dupWarning && (
                <div style={styles.dupBox}>
                    <strong>⚠ Duplicate artist detected.</strong> An artist with this name, city, and location already exists:
                    <div style={{ marginTop: '8px' }}>
                        <strong>{dupWarning.name}</strong>
                        {(dupWarning.city || dupWarning.state_name || dupWarning.country_name) && (
                            <span> — {[dupWarning.city, dupWarning.state_name, dupWarning.country_name].filter(Boolean).join(', ')}</span>
                        )}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <a href={`/artists/${dupWarning.id}`} style={styles.dupLink}>→ View existing artist</a>
                    </div>
                </div>
            )}

            <div style={styles.form}>

                {/* Basic Info */}
                <div style={styles.sectionHeader}>Basic Info</div>

                <div style={styles.field}>
                    <label style={styles.label}>Artist Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Pears" style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="e.g. New Orleans" style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Country</label>
                    <select name="country_id" value={form.country_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select country...</option>
                        {picklists.countries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {form.country_id && filteredStates.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>State / Region</label>
                        <select name="state_id" value={form.state_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select state/region...</option>
                            {filteredStates.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Artist Type</label>
                        <select name="artist_type_id" value={form.artist_type_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select type...</option>
                            {picklists.artist_types.map(t => (
                                <option key={t.id} value={t.id}>{t.value}</option>
                            ))}
                        </select>
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Artist Style</label>
                        <select name="artist_style_id" value={form.artist_style_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select style...</option>
                            {picklists.artist_styles.map(s => (
                                <option key={s.id} value={s.id}>{s.value}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Bio & Image */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Bio &amp; Image</div>

                <div style={styles.field}>
                    <label style={styles.label}>Bio</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={4}
                        placeholder="A short description of the artist..."
                        style={{ ...styles.input, resize: 'vertical', lineHeight: '1.6' }}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." style={styles.input} />
                    {form.image_url && (
                        <div style={{ marginTop: '10px' }}>
                            <img src={form.image_url} alt="Preview" style={styles.previewImg} onError={e => { e.target.style.display = 'none'; }} />
                        </div>
                    )}
                </div>

                {/* Formerly Known As */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Formerly Known As</div>

                {formerlyKnownAs.map((fka, index) => (
                    <div key={index} style={styles.repeatRow}>
                        <input
                            value={fka.name}
                            onChange={e => updateFka(index, e.target.value)}
                            placeholder="Former name..."
                            style={{ ...styles.input, flex: 1 }}
                        />
                        <button onClick={() => removeFka(index)} style={styles.removeButton}>✕</button>
                    </div>
                ))}
                <button onClick={addFka} style={styles.addRowButton}>+ Add Name</button>

                {/* Hometowns */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Hometowns</div>
                <div style={styles.sectionHint}>Additional cities this artist is associated with.</div>

                {hometowns.map((ht, index) => {
                    const htStates = getFilteredStatesForHometown(ht.country_id);
                    return (
                        <div key={index} style={styles.repeatCard}>
                            <div style={styles.repeatCardRow}>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.miniLabel}>City</label>
                                    <input value={ht.city} onChange={e => updateHometown(index, 'city', e.target.value)} placeholder="City..." style={styles.input} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.miniLabel}>Country</label>
                                    <select value={ht.country_id} onChange={e => updateHometown(index, 'country_id', e.target.value)} style={styles.input}>
                                        <option value="">Select...</option>
                                        {picklists.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                {ht.country_id && htStates.length > 0 && (
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.miniLabel}>State / Region</label>
                                        <select value={ht.state_id} onChange={e => updateHometown(index, 'state_id', e.target.value)} style={styles.input}>
                                            <option value="">Select...</option>
                                            {htStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                )}
                                <button onClick={() => removeHometown(index)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                            </div>
                        </div>
                    );
                })}
                <button onClick={addHometown} style={styles.addRowButton}>+ Add Hometown</button>

                {/* Links */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Links</div>
                <div style={styles.sectionHint}>Website, Bandcamp, Spotify, social media, etc.</div>

                {links.map((link, index) => (
                    <div key={index} style={styles.repeatCard}>
                        <div style={styles.repeatCardRow}>
                            <div style={{ flex: '0 0 160px' }}>
                                <label style={styles.miniLabel}>Label</label>
                                <input value={link.description} onChange={e => updateLink(index, 'description', e.target.value)} placeholder="e.g. Bandcamp" style={styles.input} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={styles.miniLabel}>URL</label>
                                <input value={link.url} onChange={e => updateLink(index, 'url', e.target.value)} placeholder="https://..." style={styles.input} />
                            </div>
                            <button onClick={() => removeLink(index)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                        </div>
                    </div>
                ))}
                <button onClick={addLink} style={styles.addRowButton}>+ Add Link</button>

                {/* Save */}
                <div style={{ ...styles.sectionDivider, marginBottom: '24px' }} />
                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Save Artist'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '680px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' },
    sectionDivider: { borderTop: '1px solid #2a2a2a', margin: '28px 0 24px' },
    sectionHint: { fontSize: '13px', color: '#666', marginBottom: '14px', marginTop: '-10px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '13px', color: '#aaa', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    miniLabel: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    previewImg: { width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333' },
    repeatRow: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' },
    repeatCard: { backgroundColor: '#252525', border: '1px solid #333', borderRadius: '6px', padding: '14px', marginBottom: '10px' },
    repeatCardRow: { display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' },
    addRowButton: { backgroundColor: 'transparent', color: '#bb86fc', border: '1px dashed #555', borderRadius: '4px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', marginTop: '4px' },
    removeButton: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '8px 10px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginTop: '8px' },
    errorBox: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '14px 16px', color: '#f0c060', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

export default AddArtist;